import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
    AuthController,
    ChatChannelEnum,
    CompanyController,
    CompanyEntity,
    FeatureFlagController,
    UserController,
} from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaGradientTextComponent } from 'ra-ui';
import { RaUserAvatarComponent } from '@shared/components';
import { RaClickOutsideDirective, RaTooltipDirective } from '@shared/directives';
import { NotificationController } from 'src/app/domain/controllers/notification.controller';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { MenuLeftService } from '../menu-left/services/menu-left.service';
import { NotificationListComponent } from '../notification-list/notification-list.component';
import { ChatChannelController } from 'src/app/domain/controllers/chat-channel.controller';

/** Flag que liga cada canal. Com todas desligadas, o atalho some. */
const FLAG_BY_CHANNEL: Record<string, string> = {
    [ChatChannelEnum.WHATSAPP]: 'wa_integration',
    [ChatChannelEnum.TELEGRAM]: 'tg_integration',
};

/**
 * Ícone e cor da marca de cada canal, os MESMOS da tela de Integrações. Canal
 * que o backend mandar e não estiver aqui simplesmente não ganha ícone no
 * atalho, em vez de derrubar a barra.
 */
const BRAND_BY_CHANNEL: Record<string, { icon: string; color: string }> = {
    [ChatChannelEnum.WHATSAPP]: { icon: 'whatsapp', color: '#25D366' },
    [ChatChannelEnum.TELEGRAM]: { icon: 'telegram', color: '#229ED9' },
};

@Component({
    selector: 'menu-top',
    templateUrl: 'menu-top.component.html',
    styleUrl: 'menu-top.component.scss',
    imports: [
        CommonModule,
        RouterLink,
        NotificationListComponent,
        RaClickOutsideDirective,
        RaTooltipDirective,
        RaUserAvatarComponent,
        RaIconComponent,
        RaGradientTextComponent,
        TranslatePipe,
    ],
})
export class MenuTopComponent implements OnInit {
    showNotificationList = signal<boolean>(false);
    showCompanyList = signal<boolean>(false);

    // * [Company selector — exclusivo para administradores RapiDocs]
    allCompanies = signal<CompanyEntity[]>([]);
    companySearch = signal<string>('');
    companyChanging = signal<boolean>(false);

    // * [Injects]
    utilService = inject(UtilService);
    userController = inject(UserController);
    authController = inject(AuthController);
    menuLeftService = inject(MenuLeftService);
    companyController = inject(CompanyController);
    featureFlagController = inject(FeatureFlagController);
    chatChannelController = inject(ChatChannelController);

    /** Canais ligados por flag, na ordem em que o backend os manda. */
    readonly enabledChannels = computed(() =>
        (this.chatChannelController.statuses() ?? []).filter(
            status => !this.featureFlagController.isBlocked(FLAG_BY_CHANNEL[status.channel])
        )
    );

    /**
     * Atalho das integrações na barra: só para quem ainda não vinculou NENHUM
     * canal.
     *
     * Reusa a MESMA regra da apresentação da home, menos a parte de "já fechou
     * o aviso": fechar a apresentação não deve tirar o único caminho visível
     * para a funcionalidade, só parar de bloquear a tela.
     *
     * Some quando o primeiro vínculo fecha, porque aí a aba de Configurações já
     * é o lugar natural de mexer nisso.
     */
    showIntegrationsShortcut = computed(() => {
        const canais = this.enabledChannels();

        // Nenhum canal ligado por flag: não há para onde o atalho levar.
        if (!canais.length) return false;

        return canais.every(status => !status.linked);
    });

    /**
     * Marcas que o atalho mostra: um ícone por canal ligado. É o que deixa o
     * atalho ter ícone sem mentir sobre o que há do outro lado, que era o motivo
     * de ele ser só texto.
     */
    readonly shortcutBrands = computed(() =>
        this.enabledChannels()
            .map(status => BRAND_BY_CHANNEL[status.channel])
            .filter((brand): brand is { icon: string; color: string } => !!brand)
    );
    notificationController = inject(NotificationController);

    /** Indica se o usuário logado é um administrador interno do RapiDocs. */
    readonly isRapidocsAdmin = computed(() => this.userController.isRapidocsAdmin());

    /**
     * Indica se o seletor de empresas deve ficar disponível. Regra:
     * - Admin RapiDocs (perfil global): pode trocar entre TODAS as empresas da plataforma.
     * - Não-admin com MAIS DE UMA empresa: pode trocar apenas entre as suas próprias empresas.
     * - Não-admin com uma única empresa: sem seletor.
     */
    readonly canSwitchCompanies = computed(() => {
        if (this.isRapidocsAdmin()) return true;
        return (this.userController.currentUser()?.companyAccesses?.length ?? 0) > 1;
    });

    /** IDs (Set) das empresas favoritadas pelo usuário logado. */
    readonly favoriteCompanyIds = computed(
        () => new Set(this.userController.currentUser()?.favoriteCompanyIds ?? [])
    );

    /**
     * URL do logo que falhou ao carregar. O `logo` da empresa pode existir e mesmo
     * assim não abrir (arquivo removido do bucket, URL assinada vencida) — sem isto
     * o `<img>` fica com o ícone de imagem quebrada, que passa por bug de tela.
     * Guarda a URL, e não um booleano, para o fallback se desfazer sozinho quando a
     * empresa (ou o logo dela) mudar.
     */
    private readonly failedLogo = signal<string | null>(null);

    /** Logo exibível: `null` quando não existe ou quando já falhou ao carregar. */
    readonly companyLogo = computed(() => {
        const logo = this.companyController.currentCompany()?.logo;

        if (!logo || logo === this.failedLogo()) return null;

        return logo;
    });

    /** Iniciais da empresa, exibidas no lugar do logo ausente. */
    readonly companyInitials = computed(() => {
        const name = this.companyController.currentCompany()?.name?.trim();

        if (!name) return '';

        const parts = name.split(/\s+/);
        const initials = parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : parts[0][0];

        return initials.toUpperCase();
    });

    public onLogoError(): void {
        this.failedLogo.set(this.companyController.currentCompany()?.logo ?? null);
    }

    /** Empresas filtradas pela busca e ordenadas com as favoritas em primeiro. */
    readonly filteredCompanies = computed(() => {
        const term = this.companySearch().trim().toLowerCase();
        const favorites = this.favoriteCompanyIds();
        const companies = term
            ? this.allCompanies().filter(company => company.name?.toLowerCase().includes(term))
            : this.allCompanies();

        // allCompanies já vem ordenada por nome; o sort é estável, então as
        // favoritas sobem para o topo preservando a ordem alfabética interna.
        return [...companies].sort(
            (a, b) => (favorites.has(a._id) ? 0 : 1) - (favorites.has(b._id) ? 0 : 1)
        );
    });

    ngOnInit(): void {
        if (this.canSwitchCompanies()) this._loadCompanies();

        // A barra vive em todas as telas, então é o melhor lugar para consultar
        // o vínculo uma vez: a home e Configurações reaproveitam o mesmo sinal.
        // Com TODAS as flags desligadas nem consulta, para não gastar requisição
        // com a funcionalidade fora do ar. Basta um canal ligado para consultar:
        // o atalho e a apresentação dependem desta resposta.
        const algumCanalLigado = Object.values(FLAG_BY_CHANNEL).some(
            flag => !this.featureFlagController.isBlocked(flag)
        );

        if (algumCanalLigado) {
            this.chatChannelController.getStatus().subscribe();
        }
    }

    /**
     * Abre/fecha o seletor de empresas (disponível conforme `canSwitchCompanies`).
     */
    public toggleCompanyList(): void {
        if (!this.canSwitchCompanies()) return;

        this.showCompanyList.update(value => !value);

        if (this.showCompanyList()) {
            this.showNotificationList.set(false);

            // Carrega sob demanda caso ainda não tenha a lista
            if (!this.allCompanies().length) this._loadCompanies();
        }
    }

    /**
     * Troca a empresa atual do usuário.
     * @param company Empresa de destino
     */
    public changeCompany(company: CompanyEntity): void {
        if (this.companyChanging()) return;

        // Já está na empresa selecionada — apenas fecha o seletor.
        // Compara pela MESMA fonte que o backend (companyAccess.current), para evitar
        // chamar a API e receber "Usuário já está na empresa selecionada." (400).
        if (this._activeCompanyId() === String(company._id)) {
            this.showCompanyList.set(false);
            return;
        }

        this.companyChanging.set(true);
        this.authController.changeCurrentCompany(company._id).subscribe({
            next: () => {
                this.companyChanging.set(false);
                this.showCompanyList.set(false);
                this.companySearch.set('');
            },
            error: () => this.companyChanging.set(false),
        });
    }

    /**
     * Verifica se a empresa informada é a empresa atual.
     */
    public isCurrentCompany(company: CompanyEntity): boolean {
        return this._activeCompanyId() === String(company._id);
    }

    /**
     * ID da empresa atual lido da MESMA fonte que o backend usa para decidir a
     * empresa corrente (o companyAccess marcado como `current` no usuário), com
     * fallback para a empresa do CompanyController. Normalizado como string.
     */
    private _activeCompanyId(): string | null {
        const id =
            this.userController.currentUser()?.companyAccesses?.find(access => access.current)
                ?.companyId ?? this.companyController.currentCompany()?._id;

        return id != null ? String(id) : null;
    }

    /**
     * Verifica se a empresa está favoritada.
     */
    public isFavorite(company: CompanyEntity): boolean {
        return this.favoriteCompanyIds().has(company._id);
    }

    /**
     * Favorita/desfavorita uma empresa (sem disparar a troca de empresa).
     */
    public toggleFavorite(company: CompanyEntity, event: MouseEvent): void {
        event.stopPropagation();
        this.userController.toggleFavoriteCompany(company._id).subscribe();
    }

    public toggleNotificationList(): void {
        this.showNotificationList.update(value => !value);

        if (this.showNotificationList()) {
            this.showCompanyList.set(false);
        }
    }

    /**
     * Popula a lista do seletor conforme o perfil:
     * - Admin RapiDocs: busca TODAS as empresas da plataforma (`getAllForAdmin`).
     * - Não-admin (com mais de uma empresa): usa apenas as SUAS empresas, já embutidas em
     *   `companyAccesses[].company` (nome/logo) — sem chamada extra à API.
     */
    private _loadCompanies(): void {
        if (this.isRapidocsAdmin()) {
            this.companyController.getAllForAdmin().subscribe({
                next: companies => this.allCompanies.set(companies ?? []),
            });
            return;
        }

        const ownCompanies = (this.userController.currentUser()?.companyAccesses ?? [])
            .map(access => access.company)
            .filter((company): company is CompanyEntity => !!company?._id);

        this.allCompanies.set(ownCompanies);
    }
}
